import { FoodsIcon } from "@foodpilot/foods";
import { Box, Button, Collapse, useTheme } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { sanitizeRichTextHtml } from "./sanitizeRichTextHtml";

type HTMLBoxProps = {
    value: string | undefined;
    seeMoreColor?: string;
    centerImages?: boolean;
};

export const HTMLBox = (props: HTMLBoxProps) => {
    const { value, seeMoreColor, centerImages = false } = props;
    const { t } = useTranslation();
    const [seeMore, setSeeMore] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const trimmedValue = value?.trim();

    if (!trimmedValue) return null;

    const textParts = trimmedValue.split("<see-more-marker></see-more-marker>");
    if (textParts.length === 0) return null;

    return (
        <Box width={"100%"}>
            <InnerHTMLBox value={textParts[0]} centerImages={centerImages} />
            {textParts.length > 1 && (
                <Fragment>
                    <Collapse
                        in={seeMore}
                        onExited={() => buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                    >
                        <InnerHTMLBox value={textParts[1]} centerImages={centerImages} />
                    </Collapse>
                    <Button
                        ref={buttonRef}
                        variant="text"
                        onClick={() => setSeeMore(!seeMore)}
                        sx={{ color: seeMoreColor, height: "auto", padding: 0 }}
                        endIcon={<FoodsIcon size={2} icon={seeMore ? "arrowUpShort" : "arrowDownShort"} />}
                    >
                        {seeMore ?
                            t("questionnaire.helperText.see_less", "See less")
                        :   t("questionnaire.helperText.see_more", "See more")}
                    </Button>
                </Fragment>
            )}
        </Box>
    );
};

type InnerHTMLBoxProps = {
    value: string;
    centerImages: boolean;
};
const InnerHTMLBox = (props: InnerHTMLBoxProps) => {
    const { value, centerImages } = props;
    const theme = useTheme();
    const sanitizedValue = useMemo(() => sanitizeRichTextHtml(value), [value]);

    return (
        <Box
            dangerouslySetInnerHTML={{ __html: sanitizedValue }}
            sx={{
                ...theme.typography["body-regular"],
                overflow: "hidden",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                "& strong, & b": {
                    fontWeight: "bold",
                },
                "& em, & i": {
                    fontStyle: "italic",
                },
                "& u": {
                    textDecoration: "underline",
                },
                "& a": {
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                        textDecoration: "underline",
                    },
                },
                "& p": {
                    marginBlock: theme.spacing(0.5),
                },
                "& h1": {
                    ...theme.typography.h2,
                    marginBlock: theme.spacing(1),
                },
                "& h2": {
                    ...theme.typography.h3,
                    marginBlock: theme.spacing(1),
                },
                "& h3": {
                    ...theme.typography.h4,
                    marginBlock: theme.spacing(1),
                },
                "& h4, & h5, & h6": {
                    ...theme.typography.h5,
                    marginBlock: theme.spacing(1),
                },
                "& ul, & ol": {
                    paddingInlineStart: theme.spacing(3),
                },
                "& img": {
                    borderRadius: theme.spacing(1),
                    display: "block",
                    height: "auto",
                    marginInline: centerImages ? "auto" : undefined,
                    maxWidth: "100%",
                    objectFit: "contain",
                },
            }}
        />
    );
};
